// Polyfill Buffer and process for browser environment (required by Privy SDK)
import { Buffer } from 'buffer';
import process from 'process';

window.Buffer = Buffer;
global.Buffer = Buffer;
window.process = process;
global.process = process;

// Suppress known Rainbow Wallet extension errors
// Rainbow Wallet's inpage script sometimes calls chrome.runtime.sendMessage() incorrectly
// This is a known issue with the Rainbow Wallet extension, not our code
if (typeof window !== 'undefined') {
    const originalError = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
        // Suppress Rainbow Wallet's chrome.runtime.sendMessage error
        const messageStr = message ? String(message) : '';
        const sourceStr = source ? String(source) : '';
        const errorStr = error ? (error.message || String(error)) : '';
        const combinedStr = `${messageStr} ${sourceStr} ${errorStr}`;
        
        if (combinedStr.includes('chrome.runtime.sendMessage') || 
            combinedStr.includes('Extension ID') ||
            combinedStr.includes('runtime.sendMessage') ||
            combinedStr.includes('opfgelmcmbiajamepnmloijbpoleiama') ||
            combinedStr.includes('Error in invocation of runtime.sendMessage')) {
            // This is a known Rainbow Wallet extension issue - suppress it
            console.debug('Suppressed Rainbow Wallet extension error:', message);
            return true; // Suppress the error
        }
        
        // Call original error handler for other errors
        if (originalError) {
            return originalError.call(this, message, source, lineno, colno, error);
        }
        return false;
    };
    
    // Also catch unhandled promise rejections from Rainbow Wallet
    window.addEventListener('unhandledrejection', function(event) {
        const reason = event.reason;
        if (reason) {
            const message = reason?.message || String(reason);
            const stack = reason?.stack || '';
            const combinedStr = `${message} ${stack}`;
            if (combinedStr.includes('chrome.runtime.sendMessage') || 
                combinedStr.includes('Extension ID') ||
                combinedStr.includes('runtime.sendMessage') ||
                combinedStr.includes('opfgelmcmbiajamepnmloijbpoleiama') ||
                combinedStr.includes('Error in invocation of runtime.sendMessage')) {
                // Suppress Rainbow Wallet extension errors
                console.debug('Suppressed Rainbow Wallet extension promise rejection:', message);
                event.preventDefault(); // Prevent the error from being logged
                event.stopPropagation(); // Stop propagation
            }
        }
    });
    
    // Override console.error to suppress Rainbow Wallet errors in React's error overlay
    const originalConsoleError = console.error;
    console.error = function(...args) {
        const errorString = args.map(arg => {
            if (arg instanceof Error) {
                return `${arg.message} ${arg.stack || ''}`;
            }
            return String(arg);
        }).join(' ');
        if (errorString.includes('chrome.runtime.sendMessage') ||
            errorString.includes('Extension ID') ||
            errorString.includes('runtime.sendMessage') ||
            errorString.includes('opfgelmcmbiajamepnmloijbpoleiama') ||
            errorString.includes('Error in invocation of runtime.sendMessage')) {
            // Suppress Rainbow Wallet errors from console.error
            console.debug('Suppressed Rainbow Wallet extension error from console.error:', errorString);
            return;
        }
        // Call original console.error for other errors
        originalConsoleError.apply(console, args);
    };
    
    // Suppress React's error overlay for Rainbow Wallet errors
    // React's error overlay listens to window errors, so we need to catch them early
    // The error handlers above should catch most cases, but React's overlay might still show them
    // If the overlay still appears, it's a known issue with the Rainbow Wallet extension
    // and doesn't affect the functionality of the app
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { PrivyProvider } from '@privy-io/react-auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';

// Create QueryClient for React Query
const queryClient = new QueryClient();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ErrorBoundary>
    <PrivyProvider
      appId={process.env.REACT_APP_PRIVY_APP_ID}
      clientId={process.env.REACT_APP_PRIVY_CLIENT_ID || undefined}
      config={{
        // Stellar-only build: no EVM chains for Privy embedded Ethereum flows
        embeddedWallets: {
          ethereum: {
            createOnLogin: 'off',
          },
        },
        loginMethods: ['email', 'passkey', 'google'],
      }}
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </PrivyProvider>
  </ErrorBoundary>
);
